import React, { useState } from "react";
import { Button } from "antd";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";
import STRINGS from "../../utils/UserTypes";
import { GenerateReceiptRequest } from "../../api/contractsApi/generateReceipt";
import { ContractDetailButton } from "./helper";
import { CancelContractButton } from "./ContractDetailsMainButton";
import { reInitializePayment } from "../../api/contractsApi/reinitializePayment";
import { useSelector } from "react-redux";
import { isStripe } from "../../utils/paymentProvider";
import { useStripeContractCharge } from "../../hooks/useStripeContractCharge";
import PaymentElementModal from "../Stripe/PaymentElementModal";
import { resendEstimation } from "../../api/contractsApi/resendEstimation";
import { getConnectionCount } from "../../api/contractsApi/getConnectionCount";
import Disclaimer from "../BankInfo/Disclaimer";

export const ContractButtons = ({
  userData,
  contractDetail,
  setCancelModal,
  setDisputeModal,
  setModalVisible,
  setAwardToSelfModal,
  reviews,
}) => {
  const navigate = useNavigate();
  const fullUser = useSelector((state) => state?.common?.fullUser);
  const {
    chargeViaStripe,
    preparing: chargePreparing,
    modalProps: stripeChargeModalProps,
  } = useStripeContractCharge();
  const [awardChecking, setAwardChecking] = useState(false);
  const [noConnectedGigsterModal, setNoConnectedGigsterModal] = useState(false);
  const [connectionErrorModal, setConnectionErrorModal] = useState(false);

  // Re-initialize a blocked payment: under Stripe, re-confirm via the Element
  // and only re-initialize on a successful charge (R1/SEC-8).
  const handleReinitialize = async () => {
    const payload = { contractId: contractDetail?.id };
    if (isStripe() && !(await chargeViaStripe(contractDetail?.id))) {
      return;
    }
    reInitializePayment(payload, navigate);
  };

  const showResendEstimationButton =
    userData.user_type === "contractor" &&
    contractDetail?.status === "estimation_given" &&
    contractDetail?.homeownerId &&
    contractDetail?.contractorId &&
    contractDetail?.send_via;

  // G13: resend the estimation to the homeowner by email/SMS (mobile parity).
  const handleResendEstimationOnPress = async () => {
    await resendEstimation({
      userName: contractDetail?.homeowner?.user?.username || null,
      contractId: contractDetail?.id || null,
    });
  };

  // G9: awarding to a gigster lands on a gigster picker built from the contractor's
  // connections; with zero it's an empty dead-end. Check the count first and FAIL
  // CLOSED on error (the award leads to a real charge — never proceed on unverified
  // data). Mirrors mobile's useAwardConnectionGuard.
  const handleAwardToGigster = async () => {
    setAwardChecking(true);
    try {
      const count = await getConnectionCount();
      if (count > 0) {
        navigate(`/create-contract`, {
          state: { contractDetails: contractDetail, path: "pricing" },
        });
      } else {
        setNoConnectedGigsterModal(true);
      }
    } catch (e) {
      setConnectionErrorModal(true);
    } finally {
      setAwardChecking(false);
    }
  };
  const handleGenerateReceipt = async () => {
    await GenerateReceiptRequest(contractDetail?.id);
  };

  const getSubContractsCount = () => {
    const len = contractDetail["lineItem"]?.filter(
      (element) => !element.subContractId
    ).length;
    return len > 0;
  };

  const isDateBefore = (date1, date2) => {
    return date1.getTime() < date2.getTime();
  };

  const cancelFromHomeowner = () => {
    const contractStartDate =
      contractDetail?.dates_record?.[contractDetail?.dates_record?.length - 1]
        .start_date;
    const startDate = new Date(contractStartDate);
    return (
      (["work_request_sent", "receive"].includes(contractDetail?.status) ||
        (["assigned", "send"].includes(contractDetail?.status) &&
          !isDateBefore(startDate, new Date()))) &&
      userData?.user_type === STRINGS.HOMEOWNER_TYPE
    );
  };
  //
  const cancelFromContractor = () => {
    return (
      ["send", "receive"].includes(contractDetail?.status) &&
      userData?.user_type === STRINGS.CONTRACTOR_TYPE &&
      !contractDetail?.homeownerId
    );
  };

  // G11: never offer Cancel while a payment is in-flight/blocked (mobile parity).
  const cancelMode =
    (cancelFromHomeowner() || cancelFromContractor()) &&
    !["waiting", "blocked"].includes(contractDetail?.primary_status);

  return (
    <div className="mb-5">
      {contractDetail?.status === "pending_review" &&
        userData?.user_type === reviews?.[0]?.user_contracted_type &&
        !["waiting", "blocked"].includes(contractDetail?.primary_status) && (
          <div className="d-flex mt-5">
            <Button
              onClick={() => setDisputeModal(true)}
              className="w-100 fw-bold mx-1"
              style={{
                height: "56px",
                color: "#f77e0b",
                backgroundColor: "#ffffff",
                border: "1px solid #f77e0b",
              }}
            >
              <FormattedMessage id="DISPUTED" />
            </Button>
            <Button
              onClick={() => navigate(`/submit-review`, { state: { page: 1 } })}
              className="w-100 fw-bold mx-1"
              style={{
                height: "56px",
                color: "white",
                backgroundColor: "#f77e0b",
                border: "1px solid #f77e0b",
              }}
            >
              <FormattedMessage id="ACCPET_WORK" />
            </Button>
          </div>
        )}

      {(userData?.user_type === "tradesman" &&
        contractDetail?.status === "running" &&
        ![
          "contractor_changed_requirements_homeowner",
          "contractor_changed_requirements_gigster",
          "gigster_changed_requirements_homeowner",
          "homeowner_accepted_changes",
        ].includes(contractDetail?.secondary_status)) ||
      (userData?.user_type === "contractor" &&
        contractDetail?.status === "submit_to_homeowner" &&
        !["waiting", "blocked"].includes(contractDetail?.primary_status)) ? (
        <div className="mt-5">
          <Button
            onClick={() => setModalVisible(true)}
            className="w-100 fw-bold contractButtonText1"
          >
            <FormattedMessage id="SUB_WORK" />
          </Button>
        </div>
      ) : null}

      {contractDetail?.status === "work_request_sent" &&
      (userData?.user_type === "tradesman" ||
        userData?.user_type === "contractor") ? (
        <div className="mt-5">
          <Button
            onClick={() => {
              // G5: under Stripe there is no payout-verify gate on giving an
              // estimation (mobile parity) — go straight to the estimation flow.
              // Legacy AeroPay still bounces unverified users to /payment-options.
              if (isStripe() || fullUser?.receivingAccountVerify) {
                navigate(`/create-contract`, {
                  state: {
                    contractDetails: contractDetail,
                    path: "estimation",
                  },
                });
              } else {
                navigate(`/payment-options`);
              }
            }}
            className="w-100 fw-bold contractButtonText1"
          >
            <FormattedMessage id="GIVE_ESTIMATION" />
          </Button>
        </div>
      ) : null}

      {showResendEstimationButton && (
        <div>
          <ContractDetailButton
            title={
              contractDetail?.send_via === "email" ? (
                <FormattedMessage id="RESEND_ESTIMATION_EMAIL" />
              ) : (
                <FormattedMessage id="RESEND_ESTIMATION_SMS" />
              )
            }
            onClick={handleResendEstimationOnPress}
          />
        </div>
      )}

      {cancelMode && (
        <div className="mt-5">
          <CancelContractButton
            contractDetail={contractDetail}
            visible={cancelMode}
            width={"100%"}
            setCancelModal={setCancelModal}
          />
        </div>
      )}

      {!["waiting", "blocked"].includes(contractDetail?.primary_status) &&
        ["assigned", "send", "receive", "running", "pending_review"].includes(
          contractDetail?.status
        ) &&
        getSubContractsCount() &&
        userData?.user_type === "contractor" &&
        contractDetail?.secondary_status !==
          "contractor_changed_requirements_homeowner" && (
          <div className="date-div mb-5">
            {/* G10: "Do It Myself" only applies before work starts (status assigned). */}
            {contractDetail?.status === "assigned" && (
              <Button
                className="w-100 fw-bold mx-1 acceptContractBtn"
                onClick={() => {
                  setAwardToSelfModal(true);
                }}
              >
                <FormattedMessage id="Do It Myself" />
              </Button>
            )}
            <Button
              className="w-100 fw-bold mx-1 acceptContractBtn"
              loading={awardChecking}
              onClick={handleAwardToGigster}
            >
              <FormattedMessage id="AWARD_TO_GIGSTER" />
            </Button>
          </div>
        )}

      {userData?.user_type === STRINGS.CONTRACTOR_TYPE &&
      contractDetail?.status === "disputed" ? (
        <div className="mt-5">
          <Button
            onClick={() => navigate(`/submit-review`, { state: { page: 1 } })}
            className="w-100 fw-bold contractButtonText1"
          >
            <FormattedMessage id="APPROVED" />
          </Button>
        </div>
      ) : null}
      {contractDetail?.primary_status === "blocked" &&
      ((userData?.user_type === "homeowner" &&
        !contractDetail?.parentContractId) ||
        (userData?.user_type === "contractor" &&
          !contractDetail?.homeownerId)) ? (
        <div>
          <Button
            className="w-100 fw-bold contractButtonText1"
            onClick={handleReinitialize}
            loading={chargePreparing}
          >
            <FormattedMessage id="re-initialize" />
          </Button>
        </div>
      ) : null}

      {!["waiting", "blocked"].includes(contractDetail?.primary_status) &&
        contractDetail?.status === "archived" &&
        ((userData?.user_type === "homeowner" &&
          !contractDetail?.parentContractId) ||
          (userData?.user_type === "contractor" &&
            !contractDetail?.homeownerId)) && (
          <div className="w-100">
            <Button
              className="w-100 fw-bold contractButtonText2"
              onClick={handleGenerateReceipt}
            >
              <FormattedMessage id="GENE_RECIPT" />
            </Button>
          </div>
        )}
      <PaymentElementModal
        {...stripeChargeModalProps}
        title={<FormattedMessage id="re-initialize" />}
      />
      <Disclaimer
        showModal={noConnectedGigsterModal}
        title={<FormattedMessage id="CONNECT_WITH_GIGSTER_FIRST_TITLE" />}
        body={<FormattedMessage id="CONNECT_WITH_GIGSTER_FIRST_BODY" />}
        cancelText={<FormattedMessage id="cancel" />}
        confirmText={<FormattedMessage id="FIND_GIGSTERS" />}
        handleClick={() => {
          setNoConnectedGigsterModal(false);
          navigate(`/chat`);
        }}
        handleCancel={() => setNoConnectedGigsterModal(false)}
      />
      <Disclaimer
        showModal={connectionErrorModal}
        title={<FormattedMessage id="PLEASE_HOLD_ON" />}
        body={<FormattedMessage id="CONNECTION_CHECK_FAILED" />}
        cancelText={<FormattedMessage id="OK" />}
        handleCancel={() => setConnectionErrorModal(false)}
      />
    </div>
  );
};
